import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateLeadInStore, loadLeadsToStore } from 'store/actions/lead';
import { useLeadOrganisationService } from 'hooks/api/agents/lead/orgnisation';
import { useLeadStatusService } from 'hooks/api/agents/lead/status';
import GmModal from 'app/shared/modal/modal';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import WebDataTable from 'app/shared/datatable/web/datatable';
import { LeadOrgView } from 'app/member/modules/lead/view';
import leadStyles from 'app/member/modules/lead/lead.module.css';
import { Board, BoardCard } from 'app/shared/board';
import { ViewToggle } from '../shared/view-toggle';
import { useResourceLabelsByKind } from '../shared/labels/useResourceLabelsByKind';
import { CreateLeadForm } from './create';
import createStyles from './create/create.module.css';
import { LEAD_COLUMNS } from './constants';
import { table_config } from './helper';
import { GridColumn, GridRow } from 'app/layouts/grid';

export const LeadsBoard = () => {
  const dispatch = useDispatch();
  const { fetchOrganisations, searchOrganisations, updateLeadStage } = useLeadOrganisationService();
  const { fetchLeadStatuses } = useLeadStatusService();
  const lead_in_store = useSelector((state) => state.lead);
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const { labels_by_resource } = useResourceLabelsByKind('lead');

  const [leads, setLeads] = useState([]);
  const [columns, setColumns] = useState(LEAD_COLUMNS);
  const [loading, setLoading] = useState(false);
  const [active_view, setActiveView] = useState('board');
  const [is_search_mode, setSearchMode] = useState(false);
  const [lead_count, setLeadCount] = useState(0);
  const [selected_lead, setSelectedLead] = useState(null);
  const [create_column, setCreateColumn] = useState(null);

  useEffect(() => {
    loadStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (active_view === 'board') handleDataRequest(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active_view]);

  useEffect(() => {
    setLeads(
      Object.values(lead_in_store).map((lead) => ({
        ...lead,
        location: lead.location || lead.city
      }))
    );
  }, [lead_in_store]);

  const loadStatuses = async () => {
    const { lead_statuses } = await fetchLeadStatuses({ query_string: 'population=100' });
    if (lead_statuses?.length) {
      setColumns(
        lead_statuses.map((status) => ({
          key: status.slug?.toLowerCase(),
          label: status.label,
          color: status.color || '#94a3b8'
        }))
      );
    }
  };

  const handleDataRequest = async (page = 0, population = 200) => {
    try {
      setLoading(true);
      const { organisations } = await fetchOrganisations({
        query_string: `page=${page}&population=${population}&sort_by=-score`
      });
      dispatch(loadLeadsToStore(organisations));
    } catch {
      dispatch(loadLeadsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleListDataRequest = async (
    page,
    population = 10,
    options = { filter_query: '', sort_query: '', reset: false }
  ) => {
    try {
      setLoading(true);
      const sort_query = options.sort_query || 'sort_by=-created_on';
      const { organisations, size } = await fetchOrganisations({
        query_string: `${sort_query}&page=${page}&population=${population}`
      });
      setLeadCount(size);

      dispatch(loadLeadsToStore(organisations));
    } catch {
      dispatch(loadLeadsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleListSearchRequest = async (keys, keyword, page, population = 10) => {
    if (!keys) return;
    try {
      setLoading(true);
      const { organisations, size } = await searchOrganisations(keys, keyword, {
        query_string: `page=${page}&population=${population}`
      });
      setLeadCount(size);

      dispatch(loadLeadsToStore(organisations));
    } catch {
      dispatch(loadLeadsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = (lead_id, status_slug) => {
    dispatch(updateLeadInStore({ id: lead_id, status_slug }));
  };

  const handleCardMove = async (lead, status_slug) => {
    const id = lead.id || lead._id;
    const previous_slug = lead.status_slug;
    const previous_updated_on = lead.updated_on;
    dispatch(updateLeadInStore({ id, status_slug, updated_on: new Date().toISOString() }));

    const result = await updateLeadStage(id, status_slug);
    if (!result) {
      dispatch(
        updateLeadInStore({ id, status_slug: previous_slug, updated_on: previous_updated_on })
      );
      return;
    }

    if (result.updated_on) {
      dispatch(updateLeadInStore({ id, updated_on: result.updated_on }));
    }
  };

  const handleDatatableAction = ({ name, type, data }) => {
    if (type === 'single' && name === 'view') setSelectedLead(data);
  };

  const list_items = [...leads].sort(
    (a, b) => new Date(b.created_on || 0) - new Date(a.created_on || 0)
  );

  const renderCard = (lead) => (
    <BoardCard
      key={lead.id || lead._id}
      title={lead.name || '—'}
      subtitle={lead.industry}
      meta={lead.location || lead.city}
      badge={lead.score || lead.score === 0 ? `★ ${lead.score}` : null}
      labels={labels_by_resource[lead.id || lead._id] || []}
      onClick={() => setSelectedLead(lead)}
    />
  );

  return (
    <>
      <GridRow num_of_columns={12}>
        <GridColumn span={3}>
          <ViewToggle active_view={active_view} onChange={setActiveView} />
        </GridColumn>
      </GridRow>

      {active_view === 'board' && (
        <Board
          columns={columns}
          items={leads}
          group_by="status_slug"
          loading={loading}
          empty_text="No leads"
          renderCard={renderCard}
          onColumnAdd={setCreateColumn}
          add_column_key="warming_up"
          draggable
          onCardMove={handleCardMove}
        />
      )}

      {active_view === 'list' &&
        (is_mobile_view ? (
          <MobileDatatable
            config={{ ...table_config, is_search_mode, items: list_items, total_count: lead_count }}
            action={handleDatatableAction}
            onClick={setSelectedLead}
            onListModeChange={setSearchMode}
            onDataRequest={handleListDataRequest}
            onSearchRequest={handleListSearchRequest}
          />
        ) : (
          <WebDataTable
            config={{ ...table_config, is_search_mode, items: list_items, total_count: lead_count }}
            action={handleDatatableAction}
            onClick={setSelectedLead}
            loading_data={loading}
            onDataRequest={handleListDataRequest}
            onSearchRequest={handleListSearchRequest}
          />
        ))}

      <GmModal
        title={selected_lead?.name || 'Lead'}
        show_modal={!!selected_lead}
        show_header={false}
        bodyClassName={leadStyles.leadViewModal}
        onClose={() => setSelectedLead(null)}
      >
        <LeadOrgView lead={selected_lead} onStageChange={handleStageChange} />
      </GmModal>

      <GmModal
        show_header={false}
        show_modal={!!create_column}
        bodyClassName={createStyles.createModal}
        onClose={() => setCreateColumn(null)}
      >
        {create_column && (
          <CreateLeadForm
            status_slug={create_column.key}
            closeModal={() => setCreateColumn(null)}
          />
        )}
      </GmModal>
    </>
  );
};
